import Select from '@components/v2/form/Select/Select';
import Switch from '@components/v2/form/Switch/Switch';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Typography from '@components/v2/ui/Typography/Typography';
import { Grid, Stack } from '@mui/material';
import LangEnum from '../../../../lang/LangEnum';
import {
  ExportModel,
  PageOrientation,
  SortBy,
} from '../../../orders/export/V2/ExportOrderModal.types';
import { usePdfBaseOptions } from '../../../orders/export/V2/hooks/usePdfBaseOptions';
import {
  FIELD_LABELS,
  PDF_EXPORT_MODELS,
  PDF_PAGE_ORIENTATION,
} from '../../../orders/export/V2/utils/exportOrder.consts';
import {
  makeGroupByEnumPrefix,
  parseGroupByEnumPrefix,
} from '../../../orders/export/V2/utils/exportOrder.utils';
import { OrderHeadersGroupByEnum } from '../../../orders/products/orderProducts.types';
import { useTranslation } from '../../../translation/TranslationContext';
import SettingContainer from '../../SettingContainer';
import { OrderPdfConfigSectionProps } from '../types/GeneralSettings.types';

const OrderPdfConfigSectionGeneralSettings = ({
  generalSettings,
  handleChangeAndBlur,
  handleChangeAndBlurList,
}: OrderPdfConfigSectionProps) => {
  const { translate } = useTranslation();
  const { groupByOptions, orderByOptions } = usePdfBaseOptions();

  const pageOrientationItems = PDF_PAGE_ORIENTATION.map(({ labelKey, value }) => ({
    value,
    label: translate(labelKey),
  }));

  const exportModelItems = PDF_EXPORT_MODELS.map(({ labelKey, value, description }) => ({
    value,
    label: translate(labelKey),
    description: translate(description!),
  }));

  const groupByItems = groupByOptions.map(({ label, value }) => ({
    value,
    label: translate(label),
  }));

  const sortByItems = orderByOptions.map(({ label, value }) => ({
    value,
    label: translate(label),
  }));

  return (
    <SettingContainer
      title={translate(LangEnum.GENERAL_SETTINGS_ORDER_PDF_CONFIG_TITLE)}
      description={LangEnum.GENERAL_SETTINGS_ORDER_PDF_CONFIG_DESCRIPTION}
    >
      <Stack sx={{ gap: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Select
              name="contractPageOrientation"
              label={translate(LangEnum.ORDER_EXPORT_ORDER_PDF_PAGE_ORIENTATION_LABEL)}
              value={generalSettings.contractPageOrientation}
              renderValue={(selected) =>
                pageOrientationItems.find((item) => item.value === selected)?.label ?? ''
              }
              onChange={(event) => {
                handleChangeAndBlur({
                  value: event.target.value as PageOrientation,
                  name: 'contractPageOrientation',
                });
              }}
            >
              {pageOrientationItems.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Select
              name="contractExportModel"
              label={translate(FIELD_LABELS.exportModel)}
              value={generalSettings.contractExportModel}
              renderValue={(selected) =>
                exportModelItems.find((item) => item.value === selected)?.label ?? ''
              }
              onChange={(event) => {
                handleChangeAndBlur({
                  value: event.target.value as ExportModel,
                  name: 'contractExportModel',
                });
              }}
            >
              {exportModelItems.map((item) => (
                <MenuItem
                  key={item.value}
                  value={item.value}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="body1">{item.label}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item.description}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Select
              name="contractGroupBy"
              label={translate(FIELD_LABELS.groupBy)}
              value={
                generalSettings.contractGroupBy ===
                  OrderHeadersGroupByEnum.COLOR_CLASSIFICATION_TYPE &&
                generalSettings.contractClassificationTypeId
                  ? makeGroupByEnumPrefix(
                      generalSettings.contractGroupBy,
                      generalSettings.contractClassificationTypeId,
                    )
                  : generalSettings.contractGroupBy
              }
              renderValue={(selected) =>
                groupByItems.find((item) => item.value === selected)?.label ?? ''
              }
              onChange={(event) => {
                const parsed = parseGroupByEnumPrefix(event.target.value as string);
                handleChangeAndBlurList([
                  {
                    value: parsed?.groupBy as OrderHeadersGroupByEnum,
                    name: 'contractGroupBy',
                  },
                  {
                    value: parsed?.classificationTypeId ?? null,
                    name: 'contractClassificationTypeId',
                  },
                ]);
              }}
            >
              {groupByItems.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Select
              name="contractSortBy"
              label={translate(FIELD_LABELS.sortBy)}
              value={generalSettings.contractSortBy}
              renderValue={(selected) =>
                sortByItems.find((item) => item.value === selected)?.label ?? ''
              }
              onChange={(event) => {
                handleChangeAndBlur({
                  value: event.target.value as SortBy,
                  name: 'contractSortBy',
                });
              }}
            >
              {sortByItems.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Switch
          label={`${translate(LangEnum.ORDER_EXPORT_ORDER_PDF_CREATE_ORDER_SUMMARY)} ${translate(LangEnum.ORDER_EXPORT_ORDER_PDF_SUMMARY_APPLICABLE)}`}
          name="contractCreateSummary"
          size="medium"
          sx={{ ml: 0 }}
          checked={generalSettings.contractCreateSummary}
          onChange={(_, checked) =>
            handleChangeAndBlur({ value: checked, name: 'contractCreateSummary' })
          }
        />
        <Switch
          label={translate(FIELD_LABELS.showSellInPrice)}
          name="contractShowSellInPrice"
          size="medium"
          sx={{ ml: 0 }}
          checked={generalSettings.contractShowSellInPrice}
          onChange={(_, checked) => {
            checked
              ? handleChangeAndBlur({ value: true, name: 'contractShowSellInPrice' })
              : handleChangeAndBlurList([
                  { value: false, name: 'contractShowSellInPrice' },
                  { value: false, name: 'contractShowPriceComposition' },
                  { value: false, name: 'contractShowSizePriceVariation' },
                ]);
          }}
        />

        <Switch
          label={translate(FIELD_LABELS.showPriceComposition)}
          name="contractShowPriceComposition"
          size="medium"
          sx={{ ml: 0 }}
          disabled={!generalSettings.contractShowSellInPrice}
          checked={generalSettings.contractShowPriceComposition}
          onChange={(_, checked) =>
            handleChangeAndBlur({ value: checked, name: 'contractShowPriceComposition' })
          }
        />

        <Switch
          label={translate(FIELD_LABELS.showSizePriceVariation)}
          name="contractShowSizePriceVariation"
          size="medium"
          sx={{ ml: 0 }}
          disabled={!generalSettings.contractShowSellInPrice}
          checked={generalSettings.contractShowSizePriceVariation}
          onChange={(_, checked) =>
            handleChangeAndBlur({ value: checked, name: 'contractShowSizePriceVariation' })
          }
        />

        <Switch
          label={translate(FIELD_LABELS.showMarkupAndSellout)}
          name="contractShowMarkupAndSellout"
          size="medium"
          sx={{ ml: 0 }}
          checked={generalSettings.contractShowMarkupAndSellout}
          onChange={(_, checked) =>
            handleChangeAndBlur({ value: checked, name: 'contractShowMarkupAndSellout' })
          }
        />
      </Stack>
    </SettingContainer>
  );
};

export default OrderPdfConfigSectionGeneralSettings;
